import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  User, 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock as ClockIcon,
  Mail,
  Phone,
  Tag,
  UserPlus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search
} from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { apiClient } from '../../services/apiClient';
import { guestAPI } from '../../services/guestAPI';
import { useNotification } from '../../hooks/useNotification';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import LoadingSpinner from '../UI/LoadingSpinner';
import Input from '../UI/Input';
import { logError } from '../../utils/errorLogger';
import Select from '../UI/Select';
import { PhoneInputWithValidation, PhoneValidationResponse } from '../UI/PhoneInputWithValidation';
import { parseE164Phone } from '../../utils/phoneValidationV2';

interface GuestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  guestId: string;
  guestName: string;
}

interface GuestNickname {
  id: string;
  nickname: string;
  host?: {
    id: string;
    name: string;
    email: string;
  } | null;
  host_name?: string | null;
  invited_by_text?: string;
  invited_by_display?: string;
  created_at: string;
  updated_at: string;
}

interface GuestEvent {
  id: string;
  event: {
    id: string;
    title: string;
    description: string;
    event_date: string | null;
    event_time: string | null;
    location: string;
    status: string;
    host: {
      id: string;
      name: string;
      email: string;
    };
    category: {
      id: string;
      name: string;
    } | null;
  };
  status: string;
  response_date: string | null;
  companions: number;
  // NEW: Role information for companion invitations
  is_companion?: boolean;
  role?: 'primary' | 'companion';
  primary_guest?: {
    id: string;
    name: string;
    nickname?: string;
    display_name: string;
  };
  relationship?: string;
  invite_sent_at?: string | null;
  response_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface CompanionHistoryItem {
  name: string;
  nickname?: string;
  phone?: string;
  email?: string;
  relationship: string;
  last_used: string;
  event_count: number;
  events: Array<{
    event_id: string;
    event_title: string;
    event_date: string;
    status: 'pending' | 'accepted' | 'rejected' | 'ignored';
    created_at: string | null;
    guest_event_id: string;
  }>;
}

interface HistoricalEvent {
  id: string;
  event_name: string;
  invited_by: string;
  language: string;
  comments: string;
  imported_from_file: string;
  import_timestamp: string;
  created_at: string;
}

interface StandaloneCompanionLink {
  linked_guest: {
    id: string;
    name: string;
    nickname?: string;
    display_name: string;
    phone?: string;
    email?: string;
  };
  host: {
    id: string;
    name: string;
  };
  relationship: string;
  created_at: string | null;
  is_standalone: boolean;
}

interface GuestDetailsData {
  guest: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  nicknames: GuestNickname[];
  events: GuestEvent[];
  historical_events?: HistoricalEvent[];
  companions: CompanionHistoryItem[];
  standalone_companion_links?: StandaloneCompanionLink[];
  total_nicknames: number;
  total_events: number;
  total_historical_events?: number;
  total_companions: number;
}

const GuestDetailsModal: React.FC<GuestDetailsModalProps> = ({
  isOpen,
  onClose,
  guestId,
  guestName
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'nicknames' | 'events' | 'companions'>('nicknames');
  const [guestData, setGuestData] = useState<GuestDetailsData | null>(null);
  const [expandedCompanions, setExpandedCompanions] = useState<Set<string>>(new Set());
  const [editingCompanion, setEditingCompanion] = useState<string | null>(null); // Now stores companion name instead of ID
  const [editFormData, setEditFormData] = useState<any>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [companionToDelete, setCompanionToDelete] = useState<{name: string, eventId: string, companionId: string} | null>(null);
  const [showDeleteConfirmInEdit, setShowDeleteConfirmInEdit] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showAddCompanionPopup, setShowAddCompanionPopup] = useState(false);
  const [newCompanionForm, setNewCompanionForm] = useState<{name: string; phone: string; email: string; relationship: string; notes: string}>({
    name: '',
    phone: '',
    email: '',
    relationship: 'friend',
    notes: ''
  });
  const [companionPhoneValidation, setCompanionPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [editCompanionPhoneValidation, setEditCompanionPhoneValidation] = useState<PhoneValidationResponse | null>(null);

  // Pagination state
  const [nicknamesPage, setNicknamesPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [companionsPage, setCompanionsPage] = useState(1);
  const [attendedAsCompanionPage, setAttendedAsCompanionPage] = useState(1);
  const [hasMoreNicknames, setHasMoreNicknames] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [hasMoreCompanions, setHasMoreCompanions] = useState(false);
  const [hasMoreAttendedAsCompanion, setHasMoreAttendedAsCompanion] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Search state
  const [eventsSearch, setEventsSearch] = useState('');
  const [companionsSearch, setCompanionsSearch] = useState('');

  // Fetch guest details
  const fetchGuestDetails = useCallback(async (bypassCache: boolean = false) => {
    if (!guestId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Reset pagination on initial fetch
      setNicknamesPage(1);
      setEventsPage(1);
      setCompanionsPage(1);
      setAttendedAsCompanionPage(1);
      
      // For companion history, we need to call apiClient directly to bypass cache if needed
      const companionHistoryPromise = bypassCache 
        ? apiClient.get(`/guests/${guestId}/companion-history/`, { cache: false, params: { page: 1, limit: 20 } })
            .then(response => response.data.data)
            .catch(() => ({ guest: { id: guestId }, companions: [], has_more: false }))
        : guestAPI.getCompanionHistory(guestId, 1, 20).catch(() => ({ guest: { id: guestId }, companions: [], has_more: false }));
      
      const [nicknamesResponse, eventsResponse, companionsResponse] = await Promise.all([
        apiClient.get(`/guests/${guestId}/nicknames/`, { cache: !bypassCache, params: { page: 1, limit: 20 } }),
        apiClient.get(`/guests/${guestId}/events/`, { cache: !bypassCache, params: { page: 1, limit: 20 } }),
        companionHistoryPromise
      ]);

      const nicknamesData = nicknamesResponse.data;
      const eventsData = eventsResponse.data;

      if (!nicknamesData.success || !eventsData.success) {
        throw new Error(nicknamesData.message || eventsData.message || 'Failed to fetch guest details');
      }

      const companionsData = companionsResponse.companions || [];
      
      // Set pagination flags
      setHasMoreNicknames(nicknamesData.has_more || false);
      setHasMoreEvents(eventsData.has_more || false);
      setHasMoreCompanions(companionsResponse.has_more || false);
      
      // Calculate has more for attended as companion
      const companionEvents = eventsData.events.filter((e: any) => e.role === 'companion');
      setHasMoreAttendedAsCompanion(false); // Will be set when loading more events
      
      setGuestData({
        guest: nicknamesData.guest,
        nicknames: nicknamesData.nicknames,
        events: eventsData.events,
        historical_events: eventsData.historical_events || [],
        companions: companionsData,
        standalone_companion_links: eventsData.standalone_companion_links || [],
        total_nicknames: nicknamesData.total_nicknames,
        total_events: eventsData.total_events,
        total_historical_events: eventsData.total_historical_events || 0,
        total_companions: companionsResponse.total_companions || companionsData.length
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch guest details');
    } finally {
      setLoading(false);
    }
  }, [guestId]);

  // Load more functions
  const loadMoreNicknames = async () => {
    if (!guestId || !hasMoreNicknames || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = nicknamesPage + 1;
      const response = await apiClient.get(`/guests/${guestId}/nicknames/`, { params: { page: nextPage, limit: 20 } });
      const data = response.data;
      if (data.success && guestData) {
        setGuestData({
          ...guestData,
          nicknames: [...guestData.nicknames, ...data.nicknames]
        });
        setNicknamesPage(nextPage);
        setHasMoreNicknames(data.has_more || false);
      }
    } catch (err) {
      logError('Failed to load more nicknames', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreEvents = async () => {
    if (!guestId || !hasMoreEvents || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = eventsPage + 1;
      const response = await apiClient.get(`/guests/${guestId}/events/`, { params: { page: nextPage, limit: 20 } });
      const data = response.data;
      if (data.success && guestData) {
        setGuestData({
          ...guestData,
          events: [...guestData.events, ...data.events]
        });
        setEventsPage(nextPage);
        setHasMoreEvents(data.has_more || false);
      }
    } catch (err) {
      logError('Failed to load more events', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreCompanions = async () => {
    if (!guestId || !hasMoreCompanions || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = companionsPage + 1;
      const response = await guestAPI.getCompanionHistory(guestId, nextPage, 20);
      if (response && guestData) {
        setGuestData({
          ...guestData,
          companions: [...guestData.companions, ...response.companions]
        });
        setCompanionsPage(nextPage);
        setHasMoreCompanions(response.has_more || false);
      }
    } catch (err) {
      logError('Failed to load more companions', err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isOpen && guestId) {
      // Invalidate cache when modal opens to ensure fresh data
      // This is especially important when companions are added from event pages
      apiClient.invalidateCache('guests');
      apiClient.invalidateCache('companion-history');
      // Fetch fresh data (bypass cache)
      fetchGuestDetails(true);
    }
  }, [isOpen, guestId, fetchGuestDetails]);

  // Handle Escape key and body scroll for Add Companion popup
  useEffect(() => {
    if (!showAddCompanionPopup) return;

      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setShowAddCompanionPopup(false);
          setNewCompanionForm({ name: '', phone: '', email: '', relationship: 'friend', notes: '' });
          setCompanionPhoneValidation(null);
        }
      };

    document.addEventListener('keydown', handleEscape);
    // Prevent body scroll when popup is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [showAddCompanionPopup]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'accepted':
      case 'confirmed':
        return 'text-green-600 bg-green-100';
      case 'declined':
      case 'rejected':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getEventStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getRelationshipColor = (relationship: string) => {
    switch (relationship.toLowerCase()) {
      case 'spouse':
        return 'bg-purple-100 text-purple-700';
      case 'partner':
        return 'bg-pink-100 text-pink-700';
      case 'child':
        return 'bg-blue-100 text-blue-700';
      case 'parent':
        return 'bg-indigo-100 text-indigo-700';
      case 'sibling':
        return 'bg-cyan-100 text-cyan-700';
      case 'friend':
        return 'bg-green-100 text-green-700';
      case 'colleague':
        return 'bg-yellow-100 text-yellow-700';
      case 'relative':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const toggleCompanionExpansion = (companionName: string) => {
    const newExpanded = new Set(expandedCompanions);
    if (newExpanded.has(companionName)) {
      newExpanded.delete(companionName);
    } else {
      newExpanded.add(companionName);
    }
    setExpandedCompanions(newExpanded);
  };

  const handleEditCompanion = async (companionName: string) => {
    if (!guestData) return;
    
    // Find the companion from the companion history
    const companion = guestData.companions.find(c => c.name === companionName);
    
    if (!companion) {
      logError('Companion not found', { companionName });
      showNotification('Companion not found', 'error');
      return;
    }
    
    try {
      setUpdating(true);
      
      // Get the first event where this companion appears to fetch full details (including notes)
      const firstEvent = companion.events[0];
      if (!firstEvent) {
        // If no events, use companion history data (without notes)
        setEditingCompanion(companionName);
        // Parse existing phone if it's in E.164 format
        let phoneValue = companion.phone || '';
        if (phoneValue && /^\d{10,15}$/.test(phoneValue.replace(/[^\d]/g, ''))) {
          // Likely E.164 format, parse it
          const { localNumber } = parseE164Phone(phoneValue);
          phoneValue = localNumber || phoneValue;
        }
        setEditFormData({
          name: companion.name,
          phone: phoneValue,
          email: companion.email || '',
          relationship: companion.relationship,
          notes: ''
        });
        setEditCompanionPhoneValidation(null); // Reset validation when loading new data
        return;
      }
      
      // Find the guest event
      const guestEvent = guestData.events.find(e => e.event.title === firstEvent.event_title);
      if (!guestEvent) {
        // Fallback to companion history data if event not found
        setEditingCompanion(companionName);
        // Parse existing phone if it's in E.164 format
        let phoneValue = companion.phone || '';
        if (phoneValue && /^\d{10,15}$/.test(phoneValue.replace(/[^\d]/g, ''))) {
          // Likely E.164 format, parse it
          const { localNumber } = parseE164Phone(phoneValue);
          phoneValue = localNumber || phoneValue;
        }
        setEditFormData({
          name: companion.name,
          phone: phoneValue,
          email: companion.email || '',
          relationship: companion.relationship,
          notes: ''
        });
        setEditCompanionPhoneValidation(null); // Reset validation when loading new data
        return;
      }
      
      // Fetch companions for this event to get full details including notes
      const companions = await guestAPI.companion.getCompanions(
        guestEvent.event.id,
        parseInt(guestEvent.id)
      );
      
      const companionInstance = companions.find(c => (c.display_name || c.guest_name) === companionName);
      
      if (companionInstance) {
        setEditingCompanion(companionName);
        // Parse existing phone if it's in E.164 format
        let phoneValue = companionInstance.guest_phone || '';
        if (phoneValue && /^\d{10,15}$/.test(phoneValue.replace(/[^\d]/g, ''))) {
          // Likely E.164 format, parse it
          const { localNumber } = parseE164Phone(phoneValue);
          phoneValue = localNumber || phoneValue;
        }
        setEditFormData({
          name: companionInstance.display_name || companionInstance.guest_name,
          phone: phoneValue,
          email: companionInstance.guest_email || '',
          relationship: companionInstance.relationship,
          notes: companionInstance.notes || ''
        });
        setEditCompanionPhoneValidation(null); // Reset validation when loading new data
      } else {
        // Fallback to companion history data
        setEditingCompanion(companionName);
        // Parse existing phone if it's in E.164 format
        let phoneValue = companion.phone || '';
        if (phoneValue && /^\d{10,15}$/.test(phoneValue.replace(/[^\d]/g, ''))) {
          // Likely E.164 format, parse it
          const { localNumber } = parseE164Phone(phoneValue);
          phoneValue = localNumber || phoneValue;
        }
        setEditFormData({
          name: companion.name,
          phone: phoneValue,
          email: companion.email || '',
          relationship: companion.relationship,
          notes: ''
        });
        setEditCompanionPhoneValidation(null); // Reset validation when loading new data
      }
    } catch (err) {
      logError('Error fetching companion details', err);
      // Fallback to companion history data
      setEditingCompanion(companionName);
      // Parse existing phone if it's in E.164 format
      let phoneValue = companion.phone || '';
      if (phoneValue && /^\d{10,15}$/.test(phoneValue.replace(/[^\d]/g, ''))) {
        // Likely E.164 format, parse it
        const { localNumber } = parseE164Phone(phoneValue);
        phoneValue = localNumber || phoneValue;
      }
      setEditFormData({
        name: companion.name,
        phone: phoneValue,
        email: companion.email || '',
        relationship: companion.relationship,
        notes: ''
      });
      setEditCompanionPhoneValidation(null); // Reset validation when loading new data
      showNotification('Loaded companion details (some fields may be missing)', 'warning');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveCompanion = async () => {
    if (!editingCompanion || !guestData) return;
    
    // Validate phone if provided - use PhoneInputWithValidation result
    if (editFormData.phone && editFormData.phone.trim()) {
      if (!editCompanionPhoneValidation || !editCompanionPhoneValidation.valid) {
        showNotification('Please enter a valid phone number', 'error');
        return;
      }
    }
    
    // Validate email if provided
    if (editFormData.email && editFormData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editFormData.email.trim())) {
        showNotification('Please enter a valid email address', 'error');
        return;
      }
    }
    
    try {
      setUpdating(true);
      
      // Find all events where this companion appears
      const companion = guestData.companions.find(c => c.name === editingCompanion);
      if (!companion) {
        showNotification('Companion not found', 'error');
        return;
      }
      
      // Check if companion has no events (standalone companion)
      const hasNoEvents = !companion.events || companion.events.length === 0;
      let updateSuccess = false;
      
      if (hasNoEvents) {
        // Update standalone companion via companion-history endpoint
        try {
          // Use normalized phone from validation if available
          const phoneToSubmit = editFormData.phone && editFormData.phone.trim() && editCompanionPhoneValidation?.valid
            ? editCompanionPhoneValidation.normalized
            : editFormData.phone || '';
          
          await apiClient.put(`/guests/${guestId}/companion-history/`, {
            original_name: editingCompanion, // Original name to find the companion
            ...editFormData,
            phone: phoneToSubmit  // Use normalized phone (E.164 format)
          });
          updateSuccess = true;
        } catch (err: any) {
          logError('Error updating standalone companion', err);
          showNotification(
            err.response?.data?.error?.message || 'Failed to update companion',
            'error'
          );
          // Don't return - refresh data anyway to show current state
        }
      } else {
        // Get all events where this companion is used
        const eventsWithCompanion = guestData.events.filter(guestEvent => 
          companion.events.some(e => e.event_title === guestEvent.event.title)
        );
        
        // Update companion in all events
        const updatePromises = eventsWithCompanion.map(async (guestEvent) => {
          try {
            // Fetch companions for this event to get the companion ID
            const companions = await guestAPI.companion.getCompanions(
              guestEvent.event.id, 
              parseInt(guestEvent.id)
            );
            
            const companionInstance = companions.find(c => (c.display_name || c.guest_name) === editingCompanion);
            if (companionInstance) {
              // Use normalized phone from validation if available
              const phoneToSubmit = editFormData.phone && editFormData.phone.trim() && editCompanionPhoneValidation?.valid
                ? editCompanionPhoneValidation.normalized
                : editFormData.phone || '';
              
              await guestAPI.companion.updateCompanion(
                guestEvent.event.id,
                parseInt(guestEvent.id),
                companionInstance.id,
                {
                  ...editFormData,
                  phone: phoneToSubmit  // Use normalized phone (E.164 format)
                }
              );
            }
          } catch (err) {
            logError(`Error updating companion in event ${guestEvent.event.title}`, err);
            // Continue with other events even if one fails
          }
        });
        
        await Promise.all(updatePromises);
        updateSuccess = true;
      }
      
      // Invalidate cache to ensure fresh data
      apiClient.invalidateCache('guests');
      apiClient.invalidateCache('companion-history');
      apiClient.invalidateCache('events');
      
      // Refresh data BEFORE showing success notification to ensure UI updates
      await fetchGuestDetails(true);
      
      // Show success notification after refresh completes
      if (updateSuccess) {
        if (hasNoEvents) {
          showNotification('Companion updated successfully', 'success');
        } else {
          showNotification('Companion updated successfully across all events', 'success');
        }
      }
      
      // Clear editing state after refresh completes
      setEditingCompanion(null);
      setEditFormData(null);
      setEditCompanionPhoneValidation(null);
    } catch (err: any) {
      logError('Error updating companion', err);
      showNotification(
        err.response?.data?.message || err.message || 'Failed to update companion',
        'error'
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCompanionFromAllEvents = async () => {
    if (!editingCompanion || !guestData) return;
    
    try {
      setUpdating(true);
      
      // Find all events where this companion appears
      const companion = guestData.companions.find(c => c.name === editingCompanion);
      if (!companion) {
        showNotification('Companion not found', 'error');
        return;
      }
      
      // Check if companion has no events (standalone companion)
      const hasNoEvents = !companion.events || companion.events.length === 0;
      
      if (hasNoEvents) {
        // Delete standalone companion via companion-history DELETE endpoint
        try {
          await apiClient.delete(`/guests/${guestId}/companion-history/?name=${encodeURIComponent(editingCompanion)}`);
          
          // Invalidate cache to ensure fresh data
          apiClient.invalidateCache('guests');
          apiClient.invalidateCache('companion-history');
          
          // Refresh data with cache bypass
          await fetchGuestDetails(true);
          setEditingCompanion(null);
          setEditFormData(null);
          setShowDeleteConfirmInEdit(false);
          showNotification('Companion deleted successfully', 'success');
          return;
        } catch (err: any) {
          logError('Error deleting standalone companion', err);
          showNotification(
            err.response?.data?.error?.message || 'Failed to delete standalone companion',
            'error'
          );
          return;
        }
      }
      
      // Get all events where this companion is used
      const eventsWithCompanion = guestData.events.filter(guestEvent => 
        companion.events.some(e => e.event_title === guestEvent.event.title)
      );
      
      if (eventsWithCompanion.length === 0) {
        showNotification('No events found for this companion', 'error');
        return;
      }
      
      // Delete companion from all events
      const deletePromises = eventsWithCompanion.map(async (guestEvent) => {
        try {
          // Fetch companions for this event to get the companion ID
          const companions = await guestAPI.companion.getCompanions(
            guestEvent.event.id, 
            parseInt(guestEvent.id)
          );
          
          const companionInstance = companions.find(c => (c.display_name || c.guest_name) === editingCompanion);
          if (companionInstance) {
            await guestAPI.companion.deleteCompanion(
              guestEvent.event.id,
              parseInt(guestEvent.id),
              companionInstance.id
            );
            return true; // Indicate successful deletion
          }
          return false;
        } catch (err) {
          logError(`Error deleting companion from event ${guestEvent.event.title}`, err);
          // Continue with other events even if one fails
          return false;
        }
      });
      
      const results = await Promise.all(deletePromises);
      const deletedCount = results.filter(r => r === true).length;
      
      if (deletedCount === 0) {
        showNotification('Failed to delete companion from any events', 'error');
        return;
      }
      
      // Invalidate cache to ensure fresh data
      apiClient.invalidateCache('guests');
      apiClient.invalidateCache('companion-history');
      apiClient.invalidateCache('events');
      
      // Refresh data with cache bypass
      await fetchGuestDetails(true);
      setEditingCompanion(null);
      setEditFormData(null);
      setShowDeleteConfirmInEdit(false);
      showNotification(`Companion deleted successfully from ${deletedCount} event(s)`, 'success');
    } catch (err: any) {
      logError('Error deleting companion', err);
      showNotification(
        err.response?.data?.message || err.message || 'Failed to delete companion',
        'error'
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCompanion = async () => {
    if (!companionToDelete || !guestData) return;
    
    try {
      setUpdating(true);
      
      // Find the guest event for this event
      const guestEvent = guestData.events.find(e => e.event.id === companionToDelete.eventId);
      if (!guestEvent) {
        logError('Could not find guest event for event');
        return;
      }
      
      await guestAPI.companion.deleteCompanion(
        companionToDelete.eventId,
        parseInt(guestEvent.id), // guestEvent.id is the guest_event ID (number)
        companionToDelete.companionId
      );
      
      // Invalidate cache to ensure fresh data
      apiClient.invalidateCache('guests');
      apiClient.invalidateCache('companion-history');
      apiClient.invalidateCache('events');
      
      // Refresh data with cache bypass
      await fetchGuestDetails(true);
      setShowConfirmDelete(false);
      setCompanionToDelete(null);
      showNotification('Companion deleted successfully', 'success');
    } catch (err: any) {
      logError('Error deleting companion', err);
      showNotification(
        err.response?.data?.message || err.message || 'Failed to delete companion',
        'error'
      );
    } finally {
      setUpdating(false);
    }
  };

  const initiateDelete = async (companionName: string, eventTitle: string, guestEventId?: string) => {
    if (!guestData) return;
    
    const guestEvent = guestData.events.find(e => e.event.title === eventTitle);
    if (!guestEvent) return;
    
    try {
      setUpdating(true);
      
      // If guest_event_id is provided (from new structure), use it directly
      if (guestEventId) {
        setCompanionToDelete({
          name: companionName,
          eventId: guestEvent.event.id,
          companionId: guestEventId
        });
        setShowConfirmDelete(true);
        setUpdating(false);
        return;
      }
      
      // Fallback: Fetch companions for this event to get the companion ID
      const companions = await guestAPI.companion.getCompanions(guestEvent.event.id, parseInt(guestEvent.id));
      
      if (!companions || !Array.isArray(companions)) {
        logError('Invalid companions data');
        showNotification('Failed to load companion details', 'error');
        setUpdating(false);
        return;
      }
      
      const companion = companions.find(c => (c.display_name || c.guest_name) === companionName);
      
      if (companion) {
        setCompanionToDelete({
          name: companionName,
          eventId: guestEvent.event.id,
          companionId: companion.id
        });
        setShowConfirmDelete(true);
      } else {
        logError('Companion not found');
        showNotification('Companion not found', 'error');
      }
    } catch (err) {
      logError('Error fetching companion details for deletion', err);
      showNotification('Failed to load companion details', 'error');
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Guest Details - ${guestName}`}
      size="3xl"
    >
      <div className={`space-y-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        {/* Guest Info Header */}
        {guestData && (
          <div className={`p-3 rounded-lg border ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center space-x-2 min-w-0">
              <div className={`p-1.5 rounded-full flex-shrink-0 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <User className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-xs sm:text-sm font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {guestName}
                </h3>
                <div className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs mt-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <div className="flex items-center space-x-1 min-w-0">
                    <Mail className="h-2.5 w-2.5 flex-shrink-0" />
                    <span className="truncate">{guestData.guest.email}</span>
                  </div>
                  <div className="flex items-center space-x-1 min-w-0">
                    <Phone className="h-2.5 w-2.5 flex-shrink-0" />
                    <span className="truncate">{guestData.guest.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="lg" />
            <span className="ml-2">Loading guest details...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200">
            <div className="flex items-center space-x-2 text-red-600">
              <XCircle className="h-5 w-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Content */}
        {guestData && !loading && !error && (
          <>
            {/* Tab Navigation */}
            <div className={`flex gap-1 rounded-lg p-0.5 ${
              theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setActiveTab('nicknames')}
                className={`flex-1 py-1.5 px-2 sm:px-3 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'nicknames'
                    ? theme === 'dark'
                      ? 'bg-gray-700 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center space-x-1 sm:space-x-1.5">
                  <Tag className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    <span className="hidden sm:inline">Nicknames</span>
                    <span className="sm:hidden">Nick</span>
                    <span className="ml-0.5">({guestData.total_nicknames})</span>
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`flex-1 py-1.5 px-2 sm:px-3 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'events'
                    ? theme === 'dark'
                      ? 'bg-gray-700 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center space-x-1 sm:space-x-1.5">
                  <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    <span className="hidden sm:inline">Events</span>
                    <span className="sm:hidden">Evt</span>
                    <span className="ml-0.5">({guestData.total_events})</span>
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('companions')}
                className={`flex-1 py-1.5 px-2 sm:px-3 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'companions'
                    ? theme === 'dark'
                      ? 'bg-gray-700 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center justify-center space-x-1 sm:space-x-1.5">
                  <UserPlus className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    <span className="hidden sm:inline">Companions</span>
                    <span className="sm:hidden">Comp</span>
                    <span className="ml-0.5">({guestData.total_companions || 0})</span>
                  </span>
                </div>
              </button>
            </div>

            {/* Nicknames Tab */}
            {activeTab === 'nicknames' && (
              <div className="space-y-4">
                {guestData.nicknames.length === 0 ? (
                  <div className={`text-center py-8 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Tag className={`h-12 w-12 mx-auto mb-4 ${
                      theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                    }`} />
                    <p>No nicknames assigned to this guest</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {guestData.nicknames.map((nickname) => (
                      <div
                        key={nickname.id}
                        className={`p-4 rounded-lg border ${
                          theme === 'dark' 
                            ? 'bg-gray-800 border-gray-700' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className={`font-medium text-base mb-2 ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>"{nickname.nickname}"</h4>
                            <div className={`text-xs space-y-1 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              <div className="flex items-center space-x-2">
                                <Users className="h-4 w-4" />
                                <span>Assigned by: {nickname.host_name || (nickname.host ? nickname.host.name : 'System')}</span>
                              </div>
                              {nickname.host && nickname.host.email && (
                                <div className="flex items-center space-x-2">
                                  <Mail className="h-4 w-4" />
                                  <span>{nickname.host.email}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <ClockIcon className="h-4 w-4" />
                                <span>Created: {formatDateTime(nickname.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Show More Button for Nicknames */}
                {hasMoreNicknames && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={loadMoreNicknames}
                      disabled={loadingMore}
                      size="sm"
                      variant="secondary"
                    >
                      {loadingMore ? 'Loading...' : `Show More Nicknames`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-4">
                {/* Search Input */}
                <div className={`relative ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  <input
                    type="text"
                    placeholder="Search by event name or host name..."
                    value={eventsSearch}
                    onChange={(e) => setEventsSearch(e.target.value)}
                    className={`w-full px-4 py-2 pl-10 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                </div>

                {/* Regular Events */}
                {guestData.events.length === 0 && (!guestData.historical_events || guestData.historical_events.length === 0) ? (
                  <div className={`text-center py-8 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Calendar className={`h-12 w-12 mx-auto mb-4 ${
                      theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                    }`} />
                    <p>This guest has not been invited to any events</p>
                  </div>
                ) : (
                  <>
                    {guestData.events.length > 0 && (
                      <div className="space-y-3">
                        {(() => {
                          const filteredEvents = guestData.events.filter((guestEvent) => {
                            if (!eventsSearch.trim()) return true;
                            const searchLower = eventsSearch.toLowerCase();
                            const eventTitle = guestEvent.event.title?.toLowerCase() || '';
                            const hostName = guestEvent.event.host?.name?.toLowerCase() || '';
                            return eventTitle.includes(searchLower) || hostName.includes(searchLower);
                          });

                          if (filteredEvents.length === 0) {
                            return (
                              <div className={`text-center py-8 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                <Search className={`h-12 w-12 mx-auto mb-4 ${
                                  theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                                }`} />
                                <p>No events found matching "{eventsSearch}"</p>
                              </div>
                            );
                          }

                          return filteredEvents.map((guestEvent) => (
                      <div
                        key={guestEvent.id}
                        className={`p-4 rounded-lg border ${
                          theme === 'dark' 
                            ? 'bg-gray-800 border-gray-700' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className={`font-medium text-base mb-1 ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>{guestEvent.event.title}</h4>
                            {guestEvent.event.description && (
                              <p className={`text-xs mb-2 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}>{guestEvent.event.description}</p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              getStatusColor(guestEvent.status)
                            }`}>
                              {guestEvent.status}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              getEventStatusColor(guestEvent.event.status)
                            }`}>
                              {guestEvent.event.status}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            {guestEvent.event.event_date && (
                              <div className={`flex items-center space-x-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <Calendar className={`h-4 w-4 ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                                <span>{formatDate(guestEvent.event.event_date)}</span>
                              </div>
                            )}
                            {guestEvent.event.event_time && (
                              <div className={`flex items-center space-x-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <Clock className={`h-4 w-4 ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                                <span>{guestEvent.event.event_time}</span>
                              </div>
                            )}
                            {guestEvent.event.location && (
                              <div className={`flex items-center space-x-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <MapPin className={`h-4 w-4 ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                                <span>{guestEvent.event.location}</span>
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div className={`flex items-center space-x-2 ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <Users className={`h-4 w-4 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                              }`} />
                              <span>Host: {guestEvent.event.host.name}</span>
                            </div>
                            {guestEvent.companions > 0 && (
                              <div className={`flex items-center space-x-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <Users className={`h-4 w-4 ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                                <span>Companions: {guestEvent.companions}</span>
                              </div>
                            )}
                            {guestEvent.response_date && (
                              <div className={`flex items-center space-x-2 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <CheckCircle className={`h-4 w-4 ${
                                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                }`} />
                                <span>Responded: {formatDateTime(guestEvent.response_date)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                        ));
                        })()}
                      </div>
                    )}

                    {/* Show More Button for Events */}
                    {hasMoreEvents && (
                      <div className="flex justify-center pt-4">
                        <Button
                          onClick={loadMoreEvents}
                          disabled={loadingMore}
                          size="sm"
                          variant="secondary"
                        >
                          {loadingMore ? 'Loading...' : `Show More Events`}
                        </Button>
                      </div>
                    )}

                    {/* Historical Events Section */}
                    {guestData.historical_events && guestData.historical_events.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-700">
                        <h3 className={`text-sm font-semibold mb-4 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Historical Events
                        </h3>
                        <div className="space-y-3">
                          {guestData.historical_events.map((historicalEvent) => (
                            <div
                              key={historicalEvent.id}
                              className={`p-4 rounded-lg border ${
                                theme === 'dark' 
                                  ? 'bg-gray-800/50 border-gray-700/50' 
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <h4 className={`font-medium text-base ${
                                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      {historicalEvent.event_name}
                                    </h4>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      theme === 'dark'
                                        ? 'bg-gray-700 text-gray-300'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}>
                                      Historical
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div className="space-y-2">
                                  {historicalEvent.invited_by && (
                                    <div className={`flex items-center space-x-2 ${
                                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                      <Users className={`h-4 w-4 ${
                                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                      }`} />
                                      <span>Invited by: {historicalEvent.invited_by}</span>
                                    </div>
                                  )}
                                  <div className={`flex items-center space-x-2 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    <Tag className={`h-4 w-4 ${
                                      theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                    }`} />
                                    <span>Language: {historicalEvent.language}</span>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <div className={`flex items-center space-x-2 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    <ClockIcon className={`h-4 w-4 ${
                                      theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                    }`} />
                                    <span>Imported: {formatDateTime(historicalEvent.import_timestamp)}</span>
                                  </div>
                                  {historicalEvent.comments && (
                                    <div className={`text-xs ${
                                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                      <span className="font-medium">Comments: </span>
                                      {historicalEvent.comments}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Companions Tab */}
            {activeTab === 'companions' && (
              <div className="space-y-4">
                {/* Search Input */}
                <div className={`relative ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  <input
                    type="text"
                    placeholder="Search by companion name or event name..."
                    value={companionsSearch}
                    onChange={(e) => setCompanionsSearch(e.target.value)}
                    className={`w-full px-4 py-2 pl-10 rounded-lg border ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                </div>

                {/* Add More Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowAddCompanionPopup(true)}
                    size="sm"
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Add More Companion
                  </Button>
                </div>

                {/* NEW SECTION: Attended as Companion */}
                {((guestData.events && guestData.events.filter(e => e.role === 'companion').length > 0) ||
                  (guestData.standalone_companion_links && guestData.standalone_companion_links.length > 0)) && (
                  <div className="space-y-2">
                    <h3 className={`text-sm font-semibold flex items-center gap-2 ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      <Users className="h-4 w-4" />
                      Attended as Companion ({
                        (guestData.events?.filter(e => e.role === 'companion').length || 0) +
                        (guestData.standalone_companion_links?.length || 0)
                      })
                    </h3>
                    <div className="space-y-2">
                      {(() => {
                        const filteredCompanionEvents = guestData.events
                          .filter(e => e.role === 'companion')
                          .filter((guestEvent) => {
                            if (!companionsSearch.trim()) return true;
                            const searchLower = companionsSearch.toLowerCase();
                            const eventTitle = guestEvent.event.title?.toLowerCase() || '';
                            const primaryGuestName = guestEvent.primary_guest?.display_name?.toLowerCase() || '';
                            return eventTitle.includes(searchLower) || primaryGuestName.includes(searchLower);
                          });

                        if (filteredCompanionEvents.length === 0 && companionsSearch.trim()) {
                          return (
                            <div className={`text-center py-4 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              <Search className={`h-8 w-8 mx-auto mb-2 ${
                                theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                              }`} />
                              <p className="text-sm">No companion events found matching "{companionsSearch}"</p>
                            </div>
                          );
                        }

                        return filteredCompanionEvents.map((guestEvent) => (
                          <div
                            key={guestEvent.id}
                            className={`p-3 rounded-lg border ${
                              theme === 'dark' 
                                ? 'bg-purple-900/20 border-purple-700/50' 
                                : 'bg-purple-50 border-purple-200'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className={`font-medium text-sm ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {guestEvent.event.title}
                                  </h4>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    guestEvent.status === 'accepted'
                                      ? theme === 'dark'
                                        ? 'bg-green-900/40 text-green-300'
                                        : 'bg-green-100 text-green-700'
                                      : guestEvent.status === 'rejected'
                                      ? theme === 'dark'
                                        ? 'bg-red-900/40 text-red-300'
                                        : 'bg-red-100 text-red-700'
                                      : theme === 'dark'
                                      ? 'bg-gray-700 text-gray-300'
                                      : 'bg-gray-200 text-gray-700'
                                  }`}>
                                    {guestEvent.status}
                                  </span>
                                </div>
                                
                                {guestEvent.primary_guest && (
                                  <div className={`text-sm mb-2 ${
                                    theme === 'dark' ? 'text-purple-200' : 'text-purple-700'
                                  }`}>
                                    With: <span className="font-medium">{guestEvent.primary_guest.display_name}</span>
                                    {guestEvent.relationship && (
                                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                                        theme === 'dark' ? 'bg-purple-800/40' : 'bg-purple-100'
                                      }`}>
                                        {guestEvent.relationship}
                                      </span>
                                    )}
                                  </div>
                                )}

                                <div className={`flex items-center gap-3 text-xs ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  {guestEvent.event.event_date && (
                                    <div className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      <span>{formatDate(guestEvent.event.event_date)}</span>
                                    </div>
                                  )}
                                  {guestEvent.event.location && (
                                    <div className="flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      <span className="truncate max-w-[150px]">{guestEvent.event.location}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    
                    {/* Standalone Companion Links (no event) */}
                    {guestData.standalone_companion_links && guestData.standalone_companion_links.length > 0 && (
                      <div className="space-y-2 mt-4">
                        {guestData.standalone_companion_links
                          .filter((link) => {
                            if (!companionsSearch.trim()) return true;
                            const searchLower = companionsSearch.toLowerCase();
                            const linkedGuestName = link.linked_guest.display_name?.toLowerCase() || '';
                            const hostName = link.host.name?.toLowerCase() || '';
                            return linkedGuestName.includes(searchLower) || hostName.includes(searchLower);
                          })
                          .map((link) => (
                            <div
                              key={`standalone-${link.linked_guest.id}`}
                              className={`p-3 rounded-lg border ${
                                theme === 'dark' 
                                  ? 'bg-purple-900/20 border-purple-700/50' 
                                  : 'bg-purple-50 border-purple-200'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className={`font-medium text-sm ${
                                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                      Linked to: {link.linked_guest.display_name}
                                    </h4>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      theme === 'dark' ? 'bg-purple-800/50 text-purple-200' : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      Standalone
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRelationshipColor(link.relationship)}`}>
                                      {link.relationship.charAt(0).toUpperCase() + link.relationship.slice(1)}
                                    </span>
                                    <span className={`text-xs ${
                                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                      Host: {link.host.name}
                                    </span>
                                  </div>
                                  {link.linked_guest.phone && (
                                    <div className={`text-xs ${
                                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                      <Phone className="h-3 w-3 inline mr-1" />
                                      {link.linked_guest.phone}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Divider if both sections exist */}
                {((guestData.events && guestData.events.filter(e => e.role === 'companion').length > 0) ||
                  (guestData.standalone_companion_links && guestData.standalone_companion_links.length > 0)) && 
                 guestData.companions && guestData.companions.length > 0 && (
                  <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`} />
                )}

                {/* EXISTING SECTION: Brought Companions */}
                {guestData.companions && guestData.companions.length > 0 && (
                  <div className="space-y-2">
                    <h3 className={`text-sm font-semibold flex items-center gap-2 ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      <UserPlus className="h-4 w-4" />
                      Brought Companions ({guestData.companions.length})
                    </h3>
                  </div>
                )}

                {guestData.companions && guestData.companions.length === 0 && 
                 (!guestData.events || guestData.events.filter(e => e.role === 'companion').length === 0) ? (
                  <div className={`text-center py-8 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <UserPlus className={`h-12 w-12 mx-auto mb-4 ${
                      theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                    }`} />
                    <p>No companion history</p>
                  </div>
                ) : null}
                
                {guestData.companions && guestData.companions.length > 0 && (
                  <div className="space-y-3">
                    {(() => {
                      const filteredCompanions = guestData.companions
                        ?.filter((companion) => {
                          if (!companionsSearch.trim()) return true;
                          const searchLower = companionsSearch.toLowerCase();
                          const companionName = companion.name?.toLowerCase() || '';
                          const companionNickname = companion.nickname?.toLowerCase() || '';
                          // Also search in event titles
                          const hasEventMatch = companion.events?.some(
                            event => event.event_title?.toLowerCase().includes(searchLower)
                          );
                          return companionName.includes(searchLower) || 
                                 companionNickname.includes(searchLower) || 
                                 hasEventMatch;
                        });

                      if (filteredCompanions.length === 0 && companionsSearch.trim()) {
                        return (
                          <div className={`text-center py-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            <Search className={`h-8 w-8 mx-auto mb-2 ${
                              theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                            }`} />
                            <p className="text-sm">No companions found matching "{companionsSearch}"</p>
                          </div>
                        );
                      }

                      return filteredCompanions.map((companion, index) => (
                      <div
                        key={`${companion.name}-${index}`}
                        className={`p-4 rounded-lg border ${
                          theme === 'dark' 
                            ? 'bg-gray-800 border-gray-700' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className={`font-medium text-base ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                              }`}>{companion.name}</h4>
                              {companion.nickname && (
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  theme === 'dark' 
                                    ? 'bg-purple-900/30 text-purple-300' 
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {companion.nickname}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRelationshipColor(companion.relationship)}`}>
                                {companion.relationship.charAt(0).toUpperCase() + companion.relationship.slice(1)}
                              </span>
                              <span className={`text-xs ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                Used in {companion.event_count} {companion.event_count === 1 ? 'event' : 'events'}
                              </span>
                            </div>
                            
                            {/* Contact Info */}
                            {(companion.phone || companion.email) && (
                              <div className={`flex flex-wrap gap-x-4 gap-y-1 text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {companion.phone && (
                                  <div className="flex items-center space-x-1">
                                    <Phone className="h-3 w-3" />
                                    <span>{companion.phone}</span>
                                  </div>
                                )}
                                {companion.email && (
                                  <div className="flex items-center space-x-1">
                                    <Mail className="h-3 w-3" />
                                    <span>{companion.email}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-row items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCompanion(companion.name)}
                              className="h-8 px-2 min-w-[32px]"
                              disabled={updating}
                              title="Edit companion details"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleCompanionExpansion(companion.name)}
                              className="text-xs"
                            >
                              {expandedCompanions.has(companion.name) ? (
                                <>
                                  <ChevronUp className="h-3 w-3 mr-1" />
                                  Hide events
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-3 w-3 mr-1" />
                                  Show events
                                </>
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Expandable Event History */}
                        {expandedCompanions.has(companion.name) && (
                          <div className={`mt-3 pt-3 border-t ${
                            theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                          }`}>
                            <div className="space-y-2">
                              {companion.events.map((event, eventIndex) => (
                                <div
                                  key={eventIndex}
                                  className={`p-2 rounded ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className={`font-medium ${
                                        theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                                      }`}>{event.event_title}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                                        event.status === 'accepted'
                                          ? theme === 'dark'
                                            ? 'bg-green-900/30 text-green-300'
                                            : 'bg-green-100 text-green-700'
                                          : event.status === 'rejected'
                                          ? theme === 'dark'
                                            ? 'bg-red-900/30 text-red-300'
                                            : 'bg-red-100 text-red-700'
                                          : theme === 'dark'
                                          ? 'bg-gray-700 text-gray-400'
                                          : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {event.status}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className={`text-xs ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                      }`}>
                                        {formatDate(event.event_date)}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => initiateDelete(companion.name, event.event_title, event.guest_event_id)}
                                        className="h-6 px-2 min-w-[24px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                        disabled={updating}
                                        title="Delete companion from this event"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ));
                    })()}
                  </div>
                )}
                {/* Show More Button for Brought Companions */}
                {hasMoreCompanions && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={loadMoreCompanions}
                      disabled={loadingMore}
                      size="sm"
                      variant="secondary"
                    >
                      {loadingMore ? 'Loading...' : `Show More Companions`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Edit Companion Modal Overlay - Rendered via Portal */}
        {editingCompanion && editFormData && createPortal(
          <div 
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            onClick={(e) => {
              // Close on overlay click
              if (e.target === e.currentTarget) {
                setEditingCompanion(null);
                setEditFormData(null);
                setEditCompanionPhoneValidation(null);
                setShowDeleteConfirmInEdit(false);
              }
            }}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            {/* Modal Content */}
            <div 
              className={`relative ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Edit Companion</h3>
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Changes will be applied to all events where this companion appears.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Phone</label>
                  <PhoneInputWithValidation
                    value={editFormData.phone || ''}
                    onChange={(phoneValue, countryCodeValue) => {
                      setEditFormData({...editFormData, phone: phoneValue});
                    }}
                    onValidationChange={setEditCompanionPhoneValidation}
                    showValidationResult={false}
                    placeholder="Enter phone number (optional)"
                    dropdownZIndex={10001}
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
                
                <div>
                  <Select
                    label="Relationship"
                    options={[
                      { value: 'spouse', label: 'Spouse' },
                      { value: 'partner', label: 'Partner' },
                      { value: 'child', label: 'Child' },
                      { value: 'parent', label: 'Parent' },
                      { value: 'sibling', label: 'Sibling' },
                      { value: 'friend', label: 'Friend' },
                      { value: 'colleague', label: 'Colleague' },
                      { value: 'relative', label: 'Relative' },
                      { value: 'other', label: 'Other' }
                    ]}
                    value={editFormData.relationship}
                    onChange={(value) => setEditFormData({...editFormData, relationship: value})}
                    placeholder="Select relationship"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Notes</label>
                  <textarea
                    value={editFormData.notes || ''}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    className={`w-full px-3 py-2 border rounded ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    rows={3}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              
              <div className="flex justify-between items-center mt-6">
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirmInEdit(true)}
                  disabled={updating}
                  className={theme === 'dark' ? 'text-white' : 'text-black'}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Companion
                </Button>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingCompanion(null);
                      setEditFormData(null);
                      setEditCompanionPhoneValidation(null);
                      setShowDeleteConfirmInEdit(false);
                    }}
                    disabled={updating}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveCompanion}
                    disabled={updating}
                    loading={updating}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Confirmation in Edit Modal - Rendered via Portal */}
        {showDeleteConfirmInEdit && editingCompanion && createPortal(
          <div 
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            onClick={(e) => {
              // Close on overlay click
              if (e.target === e.currentTarget) {
                setShowDeleteConfirmInEdit(false);
              }
            }}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            {/* Modal Content */}
            <div 
              className={`relative ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Delete Companion
              </h3>
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Are you sure you want to delete <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{editingCompanion}</strong> from all events? This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirmInEdit(false)}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeleteCompanionFromAllEvents}
                  disabled={updating}
                  loading={updating}
                  className={theme === 'dark' ? 'text-white' : 'text-black'}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Delete Confirmation Modal */}
        {showConfirmDelete && companionToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
            <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}>
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Delete Companion</h3>
              <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Are you sure you want to delete <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{companionToDelete.name}</strong>? This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirmDelete(false);
                    setCompanionToDelete(null);
                  }}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteCompanion}
                  disabled={updating}
                  className={theme === 'dark' ? 'text-white' : 'text-black'}
                >
                  {updating ? <LoadingSpinner size="sm" className="mr-2" /> : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Companion Popup - Rendered via Portal */}
        {showAddCompanionPopup && createPortal(
          <div 
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            onClick={(e) => {
              // Close on overlay click
              if (e.target === e.currentTarget) {
                setShowAddCompanionPopup(false);
                setNewCompanionForm({ name: '', phone: '', email: '', relationship: 'friend', notes: '' });
              }
            }}
          >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
            
            {/* Modal Content */}
            <div 
              className={`relative ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with Close Button */}
              <div className={`flex items-center justify-between border-b px-6 py-4 ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Add New Companion
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddCompanionPopup(false);
                    setNewCompanionForm({ name: '', phone: '', email: '', relationship: 'friend', notes: '' });
                    setCompanionPhoneValidation(null);
                  }}
                  className="p-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Scrollable Content */}
              <div className="px-6 py-4 overflow-y-auto flex-1">
                <div className="space-y-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Name *
                    </label>
                    <Input
                      value={newCompanionForm.name}
                      onChange={(e) => setNewCompanionForm({...newCompanionForm, name: e.target.value})}
                      placeholder="Companion name"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Phone
                    </label>
                    <PhoneInputWithValidation
                      value={newCompanionForm.phone || ''}
                      onChange={(phoneValue, countryCodeValue) => {
                        setNewCompanionForm({...newCompanionForm, phone: phoneValue});
                      }}
                      onValidationChange={setCompanionPhoneValidation}
                      showValidationResult={false}
                      placeholder="Enter phone number (optional)"
                      dropdownZIndex={10001}
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email
                    </label>
                    <Input
                      type="email"
                      value={newCompanionForm.email}
                      onChange={(e) => setNewCompanionForm({...newCompanionForm, email: e.target.value})}
                      placeholder="Optional email"
                    />
                  </div>
                  
                  <div>
                    <Select
                      label="Relationship"
                      options={[
                        { value: 'spouse', label: 'Spouse' },
                        { value: 'partner', label: 'Partner' },
                        { value: 'child', label: 'Child' },
                        { value: 'parent', label: 'Parent' },
                        { value: 'sibling', label: 'Sibling' },
                        { value: 'friend', label: 'Friend' },
                        { value: 'colleague', label: 'Colleague' },
                        { value: 'relative', label: 'Relative' },
                        { value: 'other', label: 'Other' }
                      ]}
                      value={newCompanionForm.relationship}
                      onChange={(value) => setNewCompanionForm({...newCompanionForm, relationship: value})}
                      placeholder="Select relationship"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Notes
                    </label>
                    <textarea
                      value={newCompanionForm.notes}
                      onChange={(e) => setNewCompanionForm({...newCompanionForm, notes: e.target.value})}
                      className={`w-full px-3 py-2 border rounded ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                      rows={3}
                      placeholder="Optional notes"
                    />
                  </div>

                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Note: This will add a standalone companion to the guest's profile without linking it to a specific event.
                  </p>
                </div>
              </div>
              
              {/* Footer with Actions */}
              <div className={`flex justify-end space-x-2 px-6 py-4 border-t ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddCompanionPopup(false);
                    setNewCompanionForm({ name: '', phone: '', email: '', relationship: 'friend', notes: '' });
                    setCompanionPhoneValidation(null);
                  }}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!newCompanionForm.name.trim()) {
                      showNotification('Name is required', 'error');
                      return;
                    }
                    
                    // Validate phone number if provided
                    let formattedPhone = '';
                    if (newCompanionForm.phone && newCompanionForm.phone.trim()) {
                      if (!companionPhoneValidation || !companionPhoneValidation.valid) {
                        showNotification('Please enter a valid phone number', 'error');
                        return;
                      }
                      
                      // Use normalized phone (E.164 format) from validation
                      formattedPhone = companionPhoneValidation.normalized;
                    }
                    
                    try {
                      setUpdating(true);
                      
                      // Create standalone companion without event
                      await apiClient.post(`/guests/${guestId}/companion-history/`, {
                        name: newCompanionForm.name,
                        phone: formattedPhone || newCompanionForm.phone || '',
                        email: newCompanionForm.email || '',
                        relationship: newCompanionForm.relationship,
                        notes: newCompanionForm.notes || ''
                      });
                      
                      // Invalidate cache to ensure fresh data
                      apiClient.invalidateCache('guests');
                      apiClient.invalidateCache('companion-history');
                      
                      showNotification('Companion added successfully', 'success');
                      setShowAddCompanionPopup(false);
                      setNewCompanionForm({ name: '', phone: '', email: '', relationship: 'friend', notes: '' });
                      setCompanionPhoneValidation(null);
                      
                      // Refresh the data with cache bypass
                      await fetchGuestDetails(true);
                    } catch (err: any) {
                      logError('Error adding companion', err);
                      showNotification(err.response?.data?.error?.message || 'Failed to add companion', 'error');
                    } finally {
                      setUpdating(false);
                    }
                  }}
                  disabled={updating}
                  loading={updating}
                >
                  Add Companion
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-6"
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default GuestDetailsModal;
